import { defineAsyncComponent, type Component } from 'vue';
import type { Features } from '~/provides/features.types';
import type { AxiosPromise } from 'axios';
import teamsClient from '~/services/teamsClient';

export type StatusFetcher = (teamId: number) => AxiosPromise<{ status: string }>;

export interface Integration {
    slug: string;
    name: string;
    tagline: string;
    featureFlag?: keyof Features;
    settingsPath?: string;
    overview: string;
    features: string[];
    logo?: Component;
    url?: string;
    getStatus?: StatusFetcher;
    documentation?: Component;
    comingSoon?: boolean;
}

export const integrations: Integration[] = [
    {
        slug: 'acuity',
        name: 'Acuity',
        tagline: 'Create contacts from meetings scheduled via Acuity.',
        featureFlag: 'acuity_integration',
        settingsPath: '/settings/integrations#/acuity',
        overview:
            'When the Acuity integration is connected, calendar appointments scheduled via Acuity will automatically create new contacts in Voxie. Capture appointment details and customer information to enable follow-up SMS communications.',
        features: [
            'Automatic contact creation from appointments',
            'Phone number, email, and name collection',
            'Appointment date and type tracking',
            'Franchise Hub group mapping for appointment types',
            'Custom data mapping support',
        ],
        logo: defineAsyncComponent(() => import('./logos/AcuityLogo.vue')),
        url: 'https://acuityscheduling.com',
        getStatus: (teamId) => teamsClient.getAcuityConnectionStatus(teamId),
        documentation: defineAsyncComponent(() => import('../integrations/acuity/AcuityDocumentation.vue')),
        comingSoon: true,
    },
    {
        slug: 'api-destinations',
        name: 'API Destinations',
        tagline: 'Send Voxie event data to external APIs in real-time.',
        featureFlag: 'api_destinations',
        settingsPath: '/settings/integrations#/api-destinations',
        overview:
            'API Destinations allow you to push Voxie events to external systems in real-time. Configure webhooks to send contact updates, message events, and other data to your own APIs or third-party services.',
        features: [
            'Real-time event delivery',
            'Configurable authentication methods',
            'Multiple destination support',
            'Delivery status monitoring',
            'Retry and failure handling',
        ],
    },
    {
        slug: 'calendly',
        name: 'Calendly',
        tagline: 'Create contacts from meetings scheduled via Calendly.',
        featureFlag: 'calendly_integration',
        settingsPath: '/settings/integrations#/calendly',
        overview:
            'When the Calendly integration is connected, meeting requests scheduled via Calendly will automatically create new contacts in Voxie. Voxie collects contact details including phone number, name, email, and time-zone from meeting invitees.',
        features: [
            'Automatic contact creation from meeting bookings',
            'Phone number, email, and name collection',
            'Meeting status and reschedule URL tracking',
            'Franchise Hub group mapping for event types',
        ],
        logo: defineAsyncComponent(() => import('./logos/CalendlyLogo.vue')),
        url: 'https://calendly.com',
        getStatus: (teamId) => teamsClient.getCalendlyConnectionStatus(teamId),
        documentation: defineAsyncComponent(() => import('../integrations/calendly/CalendlyDocumentation.vue')),
        comingSoon: true,
    },
    {
        slug: 'contact-collector',
        name: 'Contact Collector',
        tagline: 'Capture leads with embeddable signup forms.',
        settingsPath: '/settings/integrations#/contact-collector',
        overview:
            'Contact Collector provides embeddable forms that capture new contacts directly into Voxie. Add signup forms to your website or landing pages to grow your SMS subscriber list with proper opt-in consent.',
        features: [
            'Embeddable signup forms',
            'Customizable form fields',
            'Automatic opt-in consent collection',
            'Direct contact creation in Voxie',
        ],
    },
    {
        slug: 'ftp-pull',
        name: 'FTP Pull',
        tagline: 'Automatically import data from your SFTP servers.',
        settingsPath: '/settings/integrations#/ftp-pull',
        overview:
            'The FTP Pull integration allows Voxie to automatically pull data from your SFTP servers and ingest it for processing. Configure URLs to pull contacts, conversions, or group data on a scheduled basis.',
        features: [
            'Automated SFTP data retrieval',
            'Contact import from CSV files',
            'Conversion tracking data import',
            'Group membership data sync',
            'Incremental file processing',
        ],
        getStatus: (teamId) => teamsClient.getFtpPullStatus(teamId),
        documentation: defineAsyncComponent(() => import('../integrations/ftp-pull/FtpPullDocumentation.vue')),
        comingSoon: true,
    },
    {
        slug: 'hubspot',
        name: 'HubSpot',
        tagline: 'Connect your HubSpot CRM to sync contact data.',
        featureFlag: 'hubspot_integration',
        settingsPath: '/settings/integrations#/hubspot',
        overview:
            'Integrate HubSpot with Voxie to sync contacts from a HubSpot Workflow into Voxie as an External List. This External List can be used immediately in Voxie for personalized SMS or MMS campaigns such as quick blast or evergreen campaigns.',
        features: ['Add HubSpot contacts to Voxie external lists', 'Map HubSpot properties to Voxie contact fields'],
        logo: defineAsyncComponent(() => import('./logos/HubspotLogo.vue')),
        url: 'https://hubspot.com',
        getStatus: (teamId) => teamsClient.getHubspotConnectionStatus(teamId),
        documentation: defineAsyncComponent(() => import('../integrations/hubspot/HubspotDocumentation.vue')),
    },
    {
        slug: 'mparticle',
        name: 'mParticle',
        tagline: 'Sync mParticle audiences and events with Voxie.',
        overview:
            'Connect mParticle to Voxie to leverage your enterprise customer data platform for SMS marketing. Stream customer events, attributes, and audiences from mParticle to trigger personalized messaging at scale.',
        features: ['Real-time event streaming', 'Customer attribute synchronization', 'Audience segment targeting'],
        logo: defineAsyncComponent(() => import('./logos/MparticleLogo.vue')),
        url: 'https://mparticle.com',
    },
    {
        slug: 'salesforce',
        name: 'Salesforce',
        tagline: 'Sync contacts with your Salesforce CRM.',
        featureFlag: 'salesforce_integration',
        settingsPath: '/settings/integrations#/salesforce',
        overview:
            'Connect Salesforce to Voxie to synchronize contact data between your CRM and SMS marketing platform. Configure field mappings to control how data flows between systems and maintain consistent customer records.',
        features: ['First name, last name, and email sync'],
        logo: defineAsyncComponent(() => import('./logos/SalesforceLogo.vue')),
        url: 'https://salesforce.com',
        comingSoon: true,
    },
    {
        slug: 'shopify',
        name: 'Shopify',
        tagline: 'Sync customers and orders from your Shopify store.',
        settingsPath: '/settings/integrations#/shopify',
        overview:
            'Connect your Shopify store to Voxie to automatically sync customer data and order information. When customers make purchases, their information flows directly into Voxie, enabling personalized SMS marketing based on their shopping behavior.',
        features: [
            'Automatic customer sync when orders are placed',
            'Real-time order data collection',
            'Customer phone number and email capture',
        ],
        logo: defineAsyncComponent(() => import('./logos/ShopifyLogo.vue')),
        url: 'https://shopify.com',
    },
];

export function getIntegrationBySlug(slug: string): Integration | undefined {
    return integrations.find((integration) => integration.slug === slug);
}
